/**
 * Calendar-invite card.
 *
 * A message carrying a text/calendar part gets a summary rendered ABOVE the
 * body — time in your zone, location, organizer, who has replied — instead of
 * mailx handing invite.ics to whatever program happens to own .ics on this
 * machine (Bob 2026-08-22 saw a bare console window and asked "is this RMF
 * Mail?"; it was not, and the fact that it could have been is the problem).
 *
 * RSVP is deliberately NOT duplicated for Google invitations. Google's own
 * HTML body carries working Yes / No / Maybe links
 * (calendar.google.com/calendar/event?action=RESPOND&rst=1|2|3), and two
 * responders racing to set PARTSTAT is worse than one. For those we show the
 * summary and point at the buttons already in the message.
 */
import type { ParsedInvite } from "@bobfrankston/mailx-types";
import { inviteHasGoogleRsvp } from "@bobfrankston/mailx-types";

const PARTSTAT_LABEL: Record<string, string> = {
    "ACCEPTED": "accepted",
    "DECLINED": "declined",
    "TENTATIVE": "maybe",
    "NEEDS-ACTION": "no reply yet",
    "DELEGATED": "delegated",
};

/** Format the event's span. An invite states wall-clock plus a zone; showing
 *  it in the reader's zone is the useful reading, but when the two differ the
 *  original is shown too — a 08:00 America/Los_Angeles meeting read as 11:00
 *  local is exactly where people mis-book. */
function formatWhen(inv: ParsedInvite): string {
    if (!inv.start) return "";
    if (inv.allDay) {
        try {
            const d = new Date(inv.start + "T00:00:00");
            return d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }) + " (all day)";
        } catch { return inv.start + " (all day)"; }
    }
    // A zoned wall-clock has no offset, so Date would read it as LOCAL time.
    // Resolve it through the stated zone to get the real instant.
    const instant = (() => {
        if (/Z$/.test(inv.start)) return new Date(inv.start);
        if (!inv.tzid) return new Date(inv.start);
        try {
            // Probe: what does this wall-clock read as in the stated zone?
            const guess = new Date(inv.start + "Z");
            const asZone = new Date(guess.toLocaleString("en-US", { timeZone: inv.tzid }));
            const asUtc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));
            return new Date(guess.getTime() + (asUtc.getTime() - asZone.getTime()));
        } catch { return new Date(inv.start); }
    })();
    if (isNaN(instant.getTime())) return inv.start;

    const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const opts: Intl.DateTimeFormatOptions = {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
    };
    let out = instant.toLocaleString(undefined, opts);
    if (inv.end && !/Z$/.test(inv.end)) {
        try {
            const endInstant = new Date(instant.getTime() + (new Date(inv.end + "Z").getTime() - new Date(inv.start + "Z").getTime()));
            out += " – " + endInstant.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
        } catch { /* single-point event */ }
    }
    if (inv.tzid && inv.tzid !== localZone) {
        out += `  (sent as ${inv.start.slice(11, 16)} ${inv.tzid})`;
    }
    return out;
}

const esc = (s: string): string =>
    (s || "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

/** Build the card, or null when there is nothing worth showing. */
export function buildInviteCard(inv: ParsedInvite | null | undefined, bodyHtml: string): HTMLElement | null {
    if (!inv || (!inv.summary && !inv.start)) return null;

    const card = document.createElement("div");
    card.className = "mv-invite";
    if (inv.method === "CANCEL") card.classList.add("mv-invite-cancelled");

    const when = formatWhen(inv);
    const isMeetLink = /^https?:///i.test(inv.location);
    const googleHandles = inv.fromGoogle && inviteHasGoogleRsvp(bodyHtml);

    const rows: string[] = [];
    if (when) rows.push(`<div class="mv-invite-when">${esc(when)}</div>`);
    if (inv.location) {
        rows.push(isMeetLink
            ? `<div class="mv-invite-row"><span class="mv-invite-label">Where</span><a href="${esc(inv.location)}" target="_blank" rel="noopener noreferrer">${esc(inv.location)}</a></div>`
            : `<div class="mv-invite-row"><span class="mv-invite-label">Where</span>${esc(inv.location)}</div>`);
    }
    if (inv.organizer.email) {
        rows.push(`<div class="mv-invite-row"><span class="mv-invite-label">Organizer</span>${esc(inv.organizer.name)}${inv.organizer.name !== inv.organizer.email ? " &lt;" + esc(inv.organizer.email) + "&gt;" : ""}</div>`);
    }
    if (inv.attendees.length) {
        const who = inv.attendees
            .map(a => `${esc(a.name || a.email)} <span class="mv-invite-partstat">(${esc(PARTSTAT_LABEL[a.status] || a.status.toLowerCase())})</span>`)
            .join(", ");
        rows.push(`<div class="mv-invite-row"><span class="mv-invite-label">Guests</span>${who}</div>`);
    }
    if (inv.rrule) rows.push(`<div class="mv-invite-row"><span class="mv-invite-label">Repeats</span><code>${esc(inv.rrule)}</code></div>`);

    const badge = inv.method === "CANCEL" ? "Cancelled"
        : inv.method === "REPLY" ? "Reply"
        : inv.status === "TENTATIVE" ? "Tentative"
        : "Invitation";

    card.innerHTML =
        `<div class="mv-invite-head"><span class="mv-invite-badge">${esc(badge)}</span>` +
        `<span class="mv-invite-summary">${esc(inv.summary || "(no title)")}</span></div>` +
        rows.join("") +
        (googleHandles
            ? `<div class="mv-invite-note">Respond with the Yes / No / Maybe buttons in the message below — they go straight to Google Calendar.</div>`
            : "");

    return card;
}
